/**
 * BarnSkills authoring API client.
 *
 * Follows the fetchWithAuth pattern in kbApi.ts. Every route requires a token now: reads and
 * validate accept any member, writes require poweruser, seed and review require creator.
 * See docs/architecture/169-barnskills-authoring-api.md.
 */
import { acquireToken } from './tokenHelper';

const API_URL = (window as any).__RUNTIME_CONFIG__?.API_URL || import.meta.env.VITE_API_URL || '/api';

async function fetchWithAuth(url: string, options?: RequestInit): Promise<Response> {
  const token = await acquireToken();
  return fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options?.headers,
    },
  });
}

/**
 * A failed save is not a transport failure — it carries the same structured issues the validate
 * endpoint returns, and the editor needs them to anchor errors against lines. Throwing a bare
 * Error would discard exactly the information the author needs.
 */
export class SkillWriteError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly errors: SkillIssue[] = [],
    public readonly warnings: SkillIssue[] = [],
    public readonly hint?: string,
  ) {
    super(message);
    this.name = 'SkillWriteError';
  }
}

async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const body = await response.text();
    let parsed: Record<string, any> = {};
    try { parsed = JSON.parse(body); } catch { /* non-JSON error body */ }
    throw new SkillWriteError(
      parsed.error || parsed.message || body || response.statusText,
      response.status,
      parsed.errors ?? [],
      parsed.warnings ?? [],
      parsed.hint,
    );
  }
  return response.json() as Promise<T>;
}

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export type SkillStatus = 'published' | 'in_review' | 'changes_requested';
export type SkillOrigin = 'catalog' | 'authored';

/** One validation issue. `field` anchors it to frontmatter, `section` to a body heading. */
export interface SkillIssue {
  code: string;
  message: string;
  field?: string;
  section?: string;
  hint?: string;
}

export interface Barnskill {
  id: string;
  name: string;
  version?: string;
  title?: string;
  category?: string;
  summary?: string;
  triggers?: string[];
  authorityRequired?: number;
  appliesTo?: string[];
  /** Authored skills carry their body; catalog skills live in the repo and may omit it. */
  content?: string;
  path?: string;
  origin: SkillOrigin;
  status?: SkillStatus;
  retired?: boolean;
  createdAt?: string;
  updatedAt?: string;
  /**
   * Authorship. `*By` is the readable handle, `*ByOid` the Entra object ID, `*ByName` the display
   * name, `*Via` the surface it was written through — see `skillAuthor.ts` for why one identity
   * field was not enough. Records written before the stamp carry only `*By`.
   */
  createdBy?: string;
  createdByOid?: string;
  createdByName?: string;
  createdVia?: string;
  updatedBy?: string;
  updatedByOid?: string;
  updatedByName?: string;
  updatedVia?: string;
  reviewedBy?: string | null;
  reviewedAt?: string | null;
  reviewNote?: string;
}

/**
 * The format version the server holds documents to. The UI shows the server's value when it has
 * one and falls back to this, so a version bump needs no UI deploy to become visible.
 */
export const FORMAT_VERSION = '1.0.0';

export interface SkillValidation {
  valid: boolean;
  /** e.g. `1.0.0` — the standard this document was checked against. */
  formatVersion?: string;
  errors: SkillIssue[];
  warnings: SkillIssue[];
  parsed: {
    name?: string;
    version?: string;
    category?: string;
    authority_required?: number;
  } | null;
}

export interface SkillWriteResult {
  name: string;
  version?: string;
  origin: SkillOrigin;
  valid: true;
  status: SkillStatus;
  /** Present only when the skill was held for review — says why, in the author's terms. */
  review?: string;
  warnings: SkillIssue[];
}

export interface SkillVersion {
  version?: string;
  archivedAt?: string;
  updatedBy?: string;
  updatedByName?: string;
  updatedVia?: string;
  title?: string;
  summary?: string;
}

/**
 * Authority at or above which a skill acts with nobody watching it run. Every skill needs a
 * Creator's approval whatever its authority; this only says how much it does once it has one.
 */
export const REVIEW_REQUIRED_AUTHORITY = 2;

/** Records written before `status` existed were all published catalog seeds. */
export function statusOf(skill: Pick<Barnskill, 'status'>): SkillStatus {
  return skill.status === 'in_review' || skill.status === 'changes_requested' ? skill.status : 'published';
}

// ─────────────────────────────────────────────────────────────────────────────
// API
// ─────────────────────────────────────────────────────────────────────────────

export const barnskillsApi = {
  async list(): Promise<Barnskill[]> {
    const res = await fetchWithAuth(`${API_URL}/barnskills`);
    return handleResponse<Barnskill[]>(res);
  },

  async get(name: string): Promise<Barnskill> {
    const res = await fetchWithAuth(`${API_URL}/barnskills/${encodeURIComponent(name)}`);
    return handleResponse<Barnskill>(res);
  },

  /**
   * Check a document without saving it. An invalid draft is a 200 with `valid: false` — the
   * editor must not have to tell "your skill has errors" apart from "the call failed".
   */
  async validate(content: string, name?: string): Promise<SkillValidation> {
    const res = await fetchWithAuth(`${API_URL}/barnskills/validate`, {
      method: 'POST',
      body: JSON.stringify({ content, ...(name ? { name } : {}) }),
    });
    return handleResponse<SkillValidation>(res);
  },

  async create(name: string, content: string): Promise<SkillWriteResult> {
    const res = await fetchWithAuth(`${API_URL}/barnskills`, {
      method: 'POST',
      body: JSON.stringify({ name, content }),
    });
    return handleResponse<SkillWriteResult>(res);
  },

  async update(name: string, content: string): Promise<SkillWriteResult> {
    const res = await fetchWithAuth(`${API_URL}/barnskills/${encodeURIComponent(name)}`, {
      method: 'PUT',
      body: JSON.stringify({ content }),
    });
    return handleResponse<SkillWriteResult>(res);
  },

  async retire(name: string): Promise<{ name: string; retired: boolean }> {
    const res = await fetchWithAuth(`${API_URL}/barnskills/${encodeURIComponent(name)}`, { method: 'DELETE' });
    return handleResponse(res);
  },

  async review(
    name: string,
    decision: 'approve' | 'request_changes',
    note?: string,
  ): Promise<{ name: string; status: SkillStatus; reviewedBy: string; reviewedAt: string }> {
    const res = await fetchWithAuth(`${API_URL}/barnskills/${encodeURIComponent(name)}/review`, {
      method: 'POST',
      body: JSON.stringify({ decision, ...(note ? { note } : {}) }),
    });
    return handleResponse(res);
  },

  async versions(name: string): Promise<{ name: string; count: number; versions: SkillVersion[] }> {
    const res = await fetchWithAuth(`${API_URL}/barnskills/${encodeURIComponent(name)}/versions`);
    return handleResponse(res);
  },
};
